import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { BookingProcessModal } from '@components/BookingProcessModal';
import {
  useModalVisibility,
  UseModalVisibilityHookReturn,
} from '@hooks/shared/useModalVisibility';
import { RootState } from '@lib/redux.lib/store';

type ContextValue = {
  bookingProcessModalVisibility: UseModalVisibilityHookReturn;
};

const TutorialContext = createContext({} as ContextValue);

const TutorialContextProvider = ({ children }: { children: ReactNode }) => {
  const bookingProcessModalVisibility = useModalVisibility();
  const { showModal: showBookingProcessModal } = bookingProcessModalVisibility;
  const showBookingModalHasBeenTriggered = useRef(false);

  const userProfile = useSelector(
    (state: RootState) => state.app.user.IUserInfoDAO
  );

  const userTutorials = useMemo(
    () => userProfile?.user_tutorials ?? [],
    [userProfile?.user_tutorials]
  );
  const bookingProcessTutorial = useMemo(
    () =>
      userTutorials.find(
        ({ tutorial_name }) => tutorial_name === 'booking_process'
      ),
    [userTutorials]
  );

  useEffect(() => {
    if (
      !showBookingModalHasBeenTriggered.current &&
      !bookingProcessTutorial?.is_completed &&
      userProfile?.bg_onboarded === true
    ) {
      showBookingProcessModal();
      showBookingModalHasBeenTriggered.current = true;
    }
  }, [
    showBookingProcessModal,
    bookingProcessTutorial?.is_completed,
    userProfile?.bg_onboarded,
  ]);

  return (
    <TutorialContext.Provider value={{ bookingProcessModalVisibility }}>
      <BookingProcessModal />
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorialContext: () => ContextValue = () =>
  useContext<ContextValue>(TutorialContext);

export { TutorialContextProvider };
